import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaClipboardList, FaChevronRight, FaCheck, FaClock, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formsService } from '../../../../services/formsService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import useForms from '../../../../hooks/community/useForms';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import { getUserId } from '../../../../services/httpService';
import { Form } from '../../../../models/forms.model';
import Button from '../../../../components/misc/Button';

export default function CommunityFormsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();
  const allForms = useForms();

  const [loadingForms, setLoadingForms] = useState(false);

  const loadForms = () => {
    if (!community?.id) return Promise.resolve();

    setLoadingForms(true);
    return formsService
      .getAllFormsForCommunity(community.id)
      .catch((error) => {
        console.error('Failed to load forms:', error);
        if (!navigator.onLine) {
          toast.error(t('error.offline'));
        } else {
          toast.error(t('error.loadFailed'));
        }
      })
      .finally(() => setLoadingForms(false));
  };

  useEffect(() => {
    loadForms();
  }, [community?.id]);

  const userId = getUserId();
  const isAdmin = community?.role === 'ADMIN';

  const hasResponded = (form: Form) => {
    return form.responses?.some((r) => r.userId === userId) || false;
  };

  const canSeeResponseCount = (form: Form) => {
    if (isAdmin) return true;
    return form.resultsVisibility !== 'PRIVATE';
  };

  const isDeadlinePassed = (form: Form) => {
    if (!form.deadline) return false;
    return new Date(form.deadline) < new Date();
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleFormClick = (form: Form) => {
    navigate(`/community/forms/${form.id}`);
  };

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  const isEmpty = allForms.length === 0 && !loadingForms;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <CommunityHeader title={t('page.forms.title')} />
        {isAdmin && (
          <Button
            variant="primary"
            className="flex items-center gap-2"
            onClick={() => navigate('/community/forms/create')}>
            <FaPlus className="h-4 w-4" />
            {t('page.dashboard.forms.create')}
          </Button>
        )}
      </div>

      {/* Content */}
      {loadingForms ? (
        <div className="flex justify-center py-12">
          <div className="text-secondary-600 text-lg">{t('page.forms.loading')}</div>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <FaClipboardList className="h-16 w-16 text-primary" />
          <h3 className="text-xl font-semibold text-primary">{t('page.forms.emptyTitle')}</h3>
          <p className="text-secondary-600 text-center">{t('page.forms.emptyText')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {allForms.map((form) => {
            const responded = hasResponded(form);
            const deadlinePassed = isDeadlinePassed(form);

            return (
              <button
                key={form.id}
                type="button"
                onClick={() => handleFormClick(form)}
                className="hover:bg-secondary-100 flex items-center justify-between rounded-lg border border-secondary-50 bg-secondary-50 px-4 py-3 text-left transition-all">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-primary">{form.title}</h3>
                    {responded && (
                      <span className="flex items-center gap-1 rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
                        <FaCheck className="h-3 w-3" />
                        {t('page.forms.responded')}
                      </span>
                    )}
                    {deadlinePassed && !responded && (
                      <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        <FaClock className="h-3 w-3" />
                        {t('page.forms.closed')}
                      </span>
                    )}
                  </div>
                  {form.description && <p className="text-secondary-600 line-clamp-2 text-sm">{form.description}</p>}
                  <div className="text-secondary-600 mt-1 flex items-center gap-4 text-xs">
                    {form.deadline && (
                      <span>
                        {t('page.forms.deadline')}: {formatDate(form.deadline)}
                      </span>
                    )}
                    {canSeeResponseCount(form) && (
                      <span>
                        {form._count?.responses || form.responses?.length || 0} {t('page.forms.responses')}
                      </span>
                    )}
                  </div>
                </div>
                <FaChevronRight className="h-5 w-5 flex-shrink-0 text-primary" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
